import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  approveParameterProposal,
  changeParameter,
  getParameterProposals,
  getParameters,
  ParameterProposal,
  ParameterView,
  rejectParameterProposal,
} from '../api/ops-console';
import { Empty, Note, OpsPage, Pill, Section } from '../../../design-system/ops';

/**
 * The operational parameter registry — ADM-16-FR-05, FR-06, FR-07.
 *
 * The prototype's closing note on this screen is the reason it exists:
 * *"Cada valor se eligió con criterio, no con evidencia."* These are reasoned
 * defaults, not measurements — exactly the kind of number that will need
 * correcting, and exactly the kind that should not need a deploy to correct.
 *
 * ADM-D01 is closed. A general number moves on its domain owner's change. A
 * high-impact one moves in two steps: a change here only proposes it, and it takes
 * effect when two distinct approvers have signed. Which of those an operator may
 * do is the server's answer, shown verbatim.
 */

/** The `.hig` row, which is this screen's own shape rather than a shared one. */
const ParameterRow: React.FC<{
  view: ParameterView;
  proposals: ParameterProposal[];
  busy: boolean;
  onChange: (next: number) => void;
  onReset: () => void;
  onApprove: (proposal: ParameterProposal) => void;
  onReject: (proposal: ParameterProposal) => void;
}> = ({ view, proposals, busy, onChange, onReset, onApprove, onReject }) => {
  const { definition: d, value, movedFromDefault } = view;
  // Rounded to the definition's own precision: 4.3 + 0.1 is 4.3999999999999995
  // in floating point, and the registry rightly refuses a floor of that.
  const nudge = (delta: number) => Number((value + delta).toFixed(d.decimals ?? 0));
  // A decision that states no bounds still needs a positive value.
  const bounded = d.min !== undefined && d.max !== undefined;
  const floor = d.min ?? Number.MIN_VALUE;

  return (
    <div className="hig-r">
      <span className={`hig-i ${movedFromDefault ? 'i-amber' : 'i-tint'}`} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5.3l3.4 2" />
        </svg>
      </span>

      <div className="hig-t">
        <b>{d.name}</b>
        <span>{d.description}</span>
        <span className="mut">
          Por defecto {d.defaultValue} {d.unit} ·{' '}
          {bounded ? `entre ${d.min} y ${d.max}` : 'límites por definir'} · origen {d.source}
          {view.version ? ` · v${view.version}` : ''}
        </span>
        {d.sensitive ? (
          <span className="mut">
            Alto impacto: un cambio queda propuesto hasta que lo aprueben dos personas distintas.
          </span>
        ) : null}
        {view.pending ? (
          <span className="mut">
            Programado: {view.pending.value} {d.unit} desde{' '}
            {new Date(view.pending.effectiveFrom).toLocaleString('es-MX')}
          </span>
        ) : null}
        {proposals.map((proposal) => (
          <span className="mut" key={proposal.proposalId}>
            Propuesto: {proposal.value} {d.unit} · {proposal.approvals.length} de 2 aprobaciones ·{' '}
            {proposal.reason}{' '}
            <button className="pm-rs" disabled={busy} onClick={() => onApprove(proposal)}>
              Aprobar
            </button>{' '}
            <button className="pm-rs" disabled={busy} onClick={() => onReject(proposal)}>
              Rechazar
            </button>
          </span>
        ))}
      </div>

      <div className="pm-c">
        <button
          className="pm-b"
          disabled={busy || nudge(-d.step) < floor}
          aria-label={`Reducir ${d.name}`}
          onClick={() => onChange(nudge(-d.step))}
        >
          −
        </button>
        <div className="pm-v">
          <b>{value}</b>
          <span>{d.unit}</span>
        </div>
        <button
          className="pm-b"
          disabled={busy || (d.max !== undefined && nudge(d.step) > d.max)}
          aria-label={`Aumentar ${d.name}`}
          onClick={() => onChange(nudge(d.step))}
        >
          +
        </button>
      </div>

      {movedFromDefault ? (
        <button className="pm-rs" disabled={busy} onClick={onReset}>
          Restaurar
        </button>
      ) : null}
      {d.sensitive ? <Pill tone="warn">{d.impactClass}</Pill> : null}
    </div>
  );
};

const AdminParameters: React.FC = () => {
  const [views, setViews] = useState<ParameterView[] | null>(null);
  const [proposals, setProposals] = useState<ParameterProposal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [current, open] = await Promise.all([getParameters(), getParameterProposals()]);
      setViews(current.parameters);
      setProposals(open.proposals);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar el registro de parámetros');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (view: ParameterView, next: number, reason: string) => {
    setBusy(true);
    setFlash(null);
    try {
      const outcome = await changeParameter(view.definition.key, {
        value: next,
        reason,
        expectedVersion: view.latestVersion,
      });
      setFlash(
        `${view.definition.name}: ${next} ${view.definition.unit}` +
          (outcome?.state === 'PROPOSED' ? ' · propuesto, pendiente de aprobación' : ''),
      );
      await load();
    } catch (e: any) {
      // The server's refusal says which authority is missing. Showing it verbatim
      // is the point — a generic "no puedes" sends an operator to ask an engineer.
      setFlash(e?.message || 'No se pudo cambiar el parámetro');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setFlash(null);
    try {
      await action();
      setFlash(done);
      await load();
    } catch (e: any) {
      setFlash(e?.message || 'No se pudo registrar la decisión');
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    // Grouped in the order the registry returns them, which is the order the
    // definitions are declared in — the console reads as the design groups it.
    const order: string[] = [];
    const byGroup: Record<string, ParameterView[]> = {};

    for (const view of views ?? []) {
      const group = view.definition.group;
      if (!byGroup[group]) {
        byGroup[group] = [];
        order.push(group);
      }
      byGroup[group].push(view);
    }

    return order.map((group) => [group, byGroup[group]] as [string, ParameterView[]]);
  }, [views]);

  if (error) {
    return (
      <OpsPage title="Parámetros operativos">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!views) {
    return (
      <OpsPage title="Parámetros operativos">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const moved = views.filter((v) => v.movedFromDefault).length;

  return (
    <OpsPage
      title="Parámetros operativos"
      subtitle="Ninguno procede de datos reales: son criterios razonados. Se ajustan aquí sin desplegar."
    >
      {flash ? <Note tone="warn">{flash}</Note> : null}

      {views.length === 0 ? (
        <Empty icon="⚙" title="Sin parámetros" />
      ) : (
        groups.map(([group, rows]) => (
          <Section title={group} key={group}>
            <div className="hig">
              {rows.map((view) => (
                <ParameterRow
                  key={view.definition.key}
                  view={view}
                  proposals={proposals.filter((p) => p.key === view.definition.key)}
                  busy={busy}
                  onApprove={(proposal) =>
                    decide(
                      () => approveParameterProposal(proposal.key, proposal.proposalId),
                      `Aprobación registrada: ${view.definition.name}`,
                    )
                  }
                  onReject={(proposal) =>
                    decide(
                      () =>
                        rejectParameterProposal(
                          proposal.key,
                          proposal.proposalId,
                          `Rechazado desde la consola: ${view.definition.name}`,
                        ),
                      `Propuesta rechazada: ${view.definition.name}`,
                    )
                  }
                  onChange={(next) =>
                    apply(view, next, `Ajuste operativo desde la consola: ${view.definition.name}`)
                  }
                  onReset={() =>
                    apply(
                      view,
                      view.definition.defaultValue,
                      `Restaurado al valor por defecto: ${view.definition.name}`,
                    )
                  }
                />
              ))}
            </div>
          </Section>
        ))
      )}

      <div className="hig-f">
        Cada valor se eligió con criterio, no con evidencia. Fijarlos en el código obligaría a un
        despliegue para corregir una decisión que probablemente haya que corregir.{' '}
        <b>
          {moved
            ? `Ahora mismo hay ${moved} ajustado${moved > 1 ? 's' : ''} sobre su valor por defecto.`
            : 'Todos están en su valor por defecto.'}
        </b>
      </div>
    </OpsPage>
  );
};

export default AdminParameters;
